import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, XCircle, Loader2, Home, Clock, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';

const ESSAIS_MAX = 10;

const PaiementSucces = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  
  const [status, setStatus] = useState('checking'); // checking, success, pending, failed
  const [paiementInfo, setPaiementInfo] = useState(null);

  // Vérifie toutes les 2 s, au plus ~20 s, puis affiche « en cours de confirmation »
  useEffect(() => {
    if (!sessionId) {
      setStatus('failed');
      return;
    }
    let annule = false;
    const verifier = async (essai) => {
      try {
        const { data } = await axios.get(`/candidats/paiement/status/${sessionId}`);
        if (annule) return;
        if (data.payment_status === 'paid') {
          setPaiementInfo(data);
          setStatus('success');
          return;
        }
        if (data.status === 'expired') {
          setStatus('failed');
          return;
        }
      } catch {
        if (annule) return;
      }
      if (essai >= ESSAIS_MAX) {
        setStatus('pending');
        return;
      }
      setTimeout(() => !annule && verifier(essai + 1), 2000);
    };
    verifier(1);
    return () => { annule = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
        {status === 'checking' && (
          <>
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
            </div>
            <h2 className="text-2xl font-bold text-[#0F172A] mb-2">Vérification en cours...</h2>
            <p className="text-slate-600">
              Nous vérifions votre paiement, veuillez patienter.
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-[#0F172A] mb-2">Paiement confirmé !</h2>
            <p className="text-slate-600 mb-4">
              Votre paiement de <span className="font-bold">{paiementInfo?.amount}€</span> pour 
              {paiementInfo?.type_paiement === 'tests_selection' 
                ? ' les tests de sélection' 
                : ' la journée de préparation'
              } a été validé.
            </p>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-green-700">
                Vous recevrez un email de confirmation avec les détails de votre inscription.
              </p>
            </div>
            <Link to="/">
              <Button className="w-full bg-[#8DC63F] hover:bg-[#7db336]">
                <Home className="mr-2 h-4 w-4" />
                Retour à mon espace
              </Button>
            </Link>
          </>
        )}

        {status === 'pending' && (
          <div data-testid="paiement-en-attente">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Clock className="h-8 w-8 text-amber-600" />
            </div>
            <h2 className="text-2xl font-bold text-[#0F172A] mb-2">Paiement en cours de confirmation</h2>
            <p className="text-slate-600 mb-6">
              Stripe n'a pas encore confirmé votre paiement. Il apparaîtra dans votre espace dès sa confirmation.
              Si vous avez été débité et qu'il n'apparaît pas d'ici une heure, contactez le centre.
            </p>
            <Button className="w-full mb-3 bg-[#8DC63F] hover:bg-[#7db336]" onClick={() => window.location.reload()} data-testid="paiement-reverifier">
              <RefreshCw className="mr-2 h-4 w-4" />
              Vérifier à nouveau
            </Button>
            <Link to="/">
              <Button className="w-full" variant="outline">
                <Home className="mr-2 h-4 w-4" />
                Retour à mon espace
              </Button>
            </Link>
          </div>
        )}

        {status === 'failed' && (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="h-8 w-8 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-[#0F172A] mb-2">Problème de paiement</h2>
            <p className="text-slate-600 mb-6">
              Nous n'avons pas pu confirmer votre paiement. 
              Si vous avez été débité, contactez-nous.
            </p>
            <Link to="/">
              <Button className="w-full" variant="outline">
                <Home className="mr-2 h-4 w-4" />
                Retour à mon espace
              </Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default PaiementSucces;
